import { useFormikContext } from "formik";
import { IVehicleInfo, IVehicleTransfer } from "@/types/vehicleTransfer.type";
import { HiTrash } from "react-icons/hi2";
import * as Yup from "yup";
import { readFileAsBase64 } from "@/pages/home/VehicleSituation";
import { convertedImageToTextPost } from "@/common/services/vehicles.api";
import { RiUpload2Line } from "react-icons/ri";
import { useState } from "react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { maskLicensePlate } from "@/common/helpers/mask.helpers";

type FormEditVehicleProps = {
  transfer: IVehicleTransfer;
  isReadOnly?: boolean;
};

const plateSchema = Yup.string()
  .transform((value) => value.toUpperCase().trim())
  .required("A placa é obrigatória.");

function FormEditVehicle({
  transfer,
  isReadOnly = false,
}: FormEditVehicleProps) {
  const { values, setFieldValue } = useFormikContext<{
    vehicles: IVehicleInfo[];
    newVehicleLicensePlate: string;
    newVehicleContent: string;
  }>();
  const { notifySuccess, notifyError } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);
  const [manualPlate, setManualPlate] = useState("");

  const handleAddVehicle = (plate: string) => {
    try {
      plateSchema.validateSync(plate);

      const formattedPlate = plate.toUpperCase().trim();

      if (values.vehicles.some((v) => v.licensePlate === formattedPlate)) {
        notifyError("Esta placa já foi adicionada.");
        return;
      }

      const newVehicle: IVehicleInfo = {
        licensePlate: formattedPlate,
        companyId: transfer.companyOriginId,
      };

      setFieldValue("vehicles", [...values.vehicles, newVehicle]);
      setFieldValue("newVehicleLicensePlate", "");
      setManualPlate("");
      notifySuccess(`Veículo ${formattedPlate} adicionado com sucesso!`);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        notifyError(error.message);
      }
    }
  };

  const handleRemoveVehicle = (indexToRemove: number) => {
    const updatedVehicles = values.vehicles.filter(
      (_, index) => index !== indexToRemove
    );
    setFieldValue("vehicles", updatedVehicles);
  };

  const handleAddManualPlate = () => {
    if (!manualPlate.trim()) {
      notifyError("Digite uma placa válida!");
      return;
    }
    handleAddVehicle(manualPlate);
  };

  // ✅ Aplicar máscara no onChange
  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.toUpperCase();
    const maskedValue = maskLicensePlate(rawValue);
    setManualPlate(maskedValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddManualPlate();
    }
  };

  const handleFileInput = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);

    const file = event.target.files?.[0];
    if (!file) {
      notifyError("Selecione um arquivo!");
      setIsLoading(false);
      return;
    }

    try {
      const base64Data = await readFileAsBase64(file);
      if (!base64Data) {
        notifyError("Erro ao converter arquivo para base64");
        return;
      }

      const licensePlateResp = await convertedImageToTextPost(base64Data);
      if (!licensePlateResp.ok) {
        notifyError(
          licensePlateResp.message || "Falha ao extrair texto da imagem. Use o campo manual abaixo."
        );
        return;
      }

      const plate = licensePlateResp.data;
      handleAddVehicle(plate);
      notifySuccess("Placa do veículo extraída com sucesso");
    } catch (error) {
      notifyError("Erro ao processar o arquivo. Use o campo manual abaixo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {!isReadOnly && (
        <div className="flex flex-col gap-4 p-4 border rounded-md">

          {/* ✅ Upload de imagem como método principal */}
          <div className="w-full max-w-[780px] mx-auto">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extrair Placa da Imagem
            </label>
            <button
              type="button"
              onClick={() => document.getElementById("fileInput")?.click()}
              disabled={isLoading}
              className={`
                flex flex-col items-center justify-center w-full h-32
                bg-gradient-to-r from-locavibe to-red-500 
                text-white rounded-2xl shadow-md 
                transition-all duration-200
                hover:shadow-lg hover:scale-[1.02]
                focus:outline-none focus:ring-2 focus:ring-blue-300 
                disabled:opacity-60 disabled:cursor-not-allowed
                disabled:hover:scale-100
              `}
            >
              {isLoading ? (
                <>
                  <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-sm font-medium">Extraindo placa...</span>
                </>
              ) : (
                <>
                  <span className="text-4xl mb-2">
                    <RiUpload2Line />
                  </span>
                  <span className="text-sm font-medium">Tire uma foto ou selecione da galeria</span>
                  <span className="text-xs opacity-80 mt-1">Método recomendado para maior praticidade</span>
                </>
              )}
            </button>
            <input
              id="fileInput"
              type="file"
              onChange={handleFileInput}
              accept="image/*"
              capture="environment"
              className="hidden"
            />
          </div>

          {/* ✅ Separador visual */}
          <div className="flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-3 text-sm text-gray-500 bg-white">Se a imagem não foi reconhecida</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* ✅ Campo manual como fallback */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Digite a Placa Manualmente
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualPlate}
                onChange={handlePlateChange} // ✅ Usando função com máscara
                onKeyPress={handleKeyPress}
                placeholder="Ex: ABC1D23 ou ABC1234"
                maxLength={8}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-locavibe focus:border-transparent font-mono text-center"
              />
              <button
                type="button"
                onClick={handleAddManualPlate}
                disabled={!manualPlate.trim()}
                className="px-4 py-2 bg-locavibe text-white rounded-md hover:bg-locavibe/90 focus:outline-none focus:ring-2 focus:ring-locavibe focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Adicionar
              </button>
            </div>
            <p className="text-xs text-gray-500">
              💡 Use este campo quando a foto não conseguir identificar a placa automaticamente
            </p>
          </div>
        </div>
      )}

      <hr className="my-6" />

      <h3 className="text-lg font-semibold mb-2">
        Veículos na Transferência ({values.vehicles.length})
      </h3>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {values.vehicles.length === 0 && (
          <p className="text-gray-500 text-center py-4">
            Nenhum veículo adicionado. 📸 Tire uma foto da placa para começar ou digite manualmente abaixo.
          </p>
        )}
        {values.vehicles.map((vehicle, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 border rounded-md bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="text-gray-600">Placa:</span>
              <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm font-semibold">
                {vehicle.licensePlate}
              </span>
            </span>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => handleRemoveVehicle(index)}
                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                title={`Remover veículo ${vehicle.licensePlate}`}
              >
                <HiTrash size={20} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default FormEditVehicle;